import { ITag } from '../../tag/index.js';
import { ApiFilterKeys } from '../../api/index.js';
import {
  AgentApplicantAuthorisationStatus,
  ApplicantDetails,
  ApplicantOfferConditionType,
  ApplicantOfferStatus,
  ApplicantResponseStatus,
  Application,
  ApplicationStatus,
  EntryPoint,
  FeeStatus,
  IApplicantOffer,
  StudyLevel,
  StudyMode,
} from '../../apply/index.js';
import { CamelToSnake, RenameKey } from '../../mapped-types/index.js';

export const EApplicantStudentNumberLogTableName = 'apply_student_numbers_log';

export enum EApplicantStudentNumberLogPGKeys {
  INTAKE = 'intake_id',
  UNI = 'uni',
  USER_ID = 'user_id',
  EVENT = 'event',
  LAST_UPDATED = 'last_updated',
  WHO = 'who',
}

export type EApplicantStudentNumberLogPGRecord = {
  [EApplicantStudentNumberLogPGKeys.INTAKE]: string;
  [EApplicantStudentNumberLogPGKeys.UNI]: string;
  [EApplicantStudentNumberLogPGKeys.USER_ID]: string;
  [EApplicantStudentNumberLogPGKeys.EVENT]: string;
  [EApplicantStudentNumberLogPGKeys.LAST_UPDATED]?: number;
  [EApplicantStudentNumberLogPGKeys.WHO]?: string;
};

export const EApplicantStudentNumberTableName = 'apply_student_numbers';

export enum EApplicantStudentNumberPGKeys {
  COMPOSITE = 'composite_key',
  INTAKE = 'intake_id',
  UNI = 'uni',

  USER_ID = 'user_id',
  STUDENT_NUM = 'apply_student_number',

  LAST_UPDATED = 'last_updated',
  WHO = 'who',
  ACTION = 'action',
}

export type EApplicantStudentNumberPGRecord = {
  [EApplicantStudentNumberPGKeys.COMPOSITE]: string;
  [EApplicantStudentNumberPGKeys.INTAKE]: string;
  [EApplicantStudentNumberPGKeys.UNI]: string;
  [EApplicantStudentNumberPGKeys.USER_ID]: string;
  [EApplicantStudentNumberPGKeys.STUDENT_NUM]?: number;

  [EApplicantStudentNumberPGKeys.LAST_UPDATED]?: number;
  [EApplicantStudentNumberPGKeys.WHO]?: string;
  [EApplicantStudentNumberPGKeys.ACTION]?: string;
};

export enum EApplicantOfferPGKeys {
  COURSE_ID = 'course_id',
  COURSE_NAME = 'course_name',
  MODE = 'course_mode',
  ENTRY_POINT = 'course_entry_point',
  KNOWN_BY = 'known_by',
  GIVEN_NAME = 'given_name',
  FAMILY_NAME = 'family_name',
  EMAIL = 'email',
  NATIONALITY = 'nationality',
  POSTCODE = 'postcode',
  CITY = 'city',
  BIRTH_COUNTRY = 'birth_country',
  RESIDENCE_COUNTRY = 'residence_country',
  APPLICATION_COUNTRY = 'application_country',
  OFFER_ID = 'offer_id',
  APPLICATION_ID = 'application_id',
  FEE_STATUS = 'fee_status',
  STATUS = 'status',
  INTAKE_ID = 'intake_id',
  UNI = 'uni',
  USER_ID = 'user_id',
  APPLIED_DATE = 'applied_date',
  ISSUE_DATE = 'issue_date',
  CONDITION_TYPE = 'condition_type',
  APPLICANT_STATUS = 'applicant_status',
  PENDING_CHANGES = 'pending_changes',
  REQUIREMENTS_IN_ASSESSMENT = 'requirements_in_assessment',
  REQUIREMENTS_WITH_CONDITION = 'requirements_with_condition',
  APPLICANT_LAST_UPDATED_AT = 'applicant_last_updated_at',
  STAGE_CHANGE_DATE = 'stage_change_date',
  NEW_DOCUMENTS_COUNT = 'new_documents_count',
  AGENT_ID = 'agent_id',
  AGENT_NAME = 'agent_name',
  TAGS = 'tags',
  AGENT_ACCESS_STATUS = 'agent_access_status',
  STUDENT_NUMBER = 'student_number',
  PARTNERS = 'partners',
  STATUS_REASON = 'status_reason',
  HIGHEST_QUALIFICATION = 'highest_qualification',
  SCHOLARSHIPS_APPLIED = 'scholarships_applied',
  POTENTIAL_DUPLICATE = 'potential_duplicate',
  PHONE_NO = 'phone_number',
  DOB = 'dob',
  STUDY_LEVEL = 'study_level',
  // PROGRAMME_CHOICE_PRIORITY = 'programme_choice_priority'
}

// PG Record of "ApplicantOffer" draws information from ApplicantDetails, Application, and IApplicantOffer
export type IApplicantOfferPGRecord = {
  p_key: string;
  pending_changes: boolean;
  requirements_in_assessment: number;
  requirements_with_condition: number;
  applicant_last_updated_at: string;
  stage_change_date: string;
  new_documents_count: number;
  agent_access_status: AgentApplicantAuthorisationStatus | null;
  status_reason: string | null;
  highest_qualification: string | null;
  scholarships_applied: number | null;
  potential_duplicate: boolean | null;
  phone_number: string | null;
  dob: string | null;
  study_level: string | null;
} & RenameKey<
  Pick<
    CamelToSnake<ApplicantDetails> &
      CamelToSnake<Omit<Application, 'applicantStatus'>> &
      CamelToSnake<IApplicantOffer['programme']> &
      CamelToSnake<ApplicantDetails['primaryResidence']> &
      CamelToSnake<Application['residentialAddress']> &
      Omit<CamelToSnake<IApplicantOffer>, 'programme' | 'requirements'>,
    | EApplicantOfferPGKeys.COURSE_ID
    | EApplicantOfferPGKeys.COURSE_NAME
    | 'student_no' // studentNo
    | 'mode'
    | 'entry_point'
    | EApplicantOfferPGKeys.KNOWN_BY
    | EApplicantOfferPGKeys.GIVEN_NAME
    | EApplicantOfferPGKeys.FAMILY_NAME
    | EApplicantOfferPGKeys.EMAIL
    | EApplicantOfferPGKeys.NATIONALITY
    | EApplicantOfferPGKeys.POSTCODE
    | EApplicantOfferPGKeys.CITY
    | 'country_of_birth' // countryOfBirth
    | 'applying_from' // applyingFrom
    | 'country_of_residence' // primaryResidence -> countryOfResidence
    | 'id'
    | EApplicantOfferPGKeys.APPLICATION_ID
    | EApplicantOfferPGKeys.FEE_STATUS
    | EApplicantOfferPGKeys.STATUS
    | EApplicantOfferPGKeys.INTAKE_ID
    | 'university_name'
    | EApplicantOfferPGKeys.USER_ID
    | EApplicantOfferPGKeys.APPLIED_DATE
    | EApplicantOfferPGKeys.ISSUE_DATE
    | EApplicantOfferPGKeys.CONDITION_TYPE
    | EApplicantOfferPGKeys.APPLICANT_STATUS
    | EApplicantOfferPGKeys.AGENT_ID
    | EApplicantOfferPGKeys.AGENT_NAME
    | EApplicantOfferPGKeys.TAGS
    | EApplicantOfferPGKeys.PARTNERS
  >,
  {
    university_name: EApplicantOfferPGKeys.UNI;
    id: EApplicantOfferPGKeys.OFFER_ID;
    mode: EApplicantOfferPGKeys.MODE;
    entry_point: EApplicantOfferPGKeys.ENTRY_POINT;
    applied_date: EApplicantOfferPGKeys.APPLIED_DATE;
    issue_date: EApplicantOfferPGKeys.ISSUE_DATE;
    condition_type: EApplicantOfferPGKeys.CONDITION_TYPE;
    applicant_status: EApplicantOfferPGKeys.APPLICANT_STATUS;

    applying_from: EApplicationPGKeys.APPLICATION_COUNTRY;
    country_of_birth: EApplicantOfferPGKeys.BIRTH_COUNTRY;
    country_of_residence: EApplicantOfferPGKeys.RESIDENCE_COUNTRY;

    pending_changes: 'pending_changes';
    requirements_in_assessment: 'requirements_in_assessment';
    requirements_with_condition: 'requirements_with_condition';
    applicant_last_updated_at: 'applicant_last_updated_at';
    stage_change_date: 'stage_change_date';
    agent_access_status: 'agent_access_status';

    student_no: EApplicantOfferPGKeys.STUDENT_NUMBER;
    status_reason: EApplicantOfferPGKeys.STATUS_REASON;
    highest_qualification: EApplicantOfferPGKeys.HIGHEST_QUALIFICATION;
    scholarships_applied: EApplicantOfferPGKeys.SCHOLARSHIPS_APPLIED;
  }
>;

export const IApplicantOfferPGTypeMap: Record<
  keyof IApplicantOfferPGRecord,
  Array<'string' | 'string[]' | 'integer' | 'float' | 'boolean' | 'jsonb' | 'date' | 'required'>
> = {
  p_key: ['string', 'required'],
  [EApplicantOfferPGKeys.UNI]: ['string', 'required'],
  [EApplicantOfferPGKeys.USER_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.OFFER_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.INTAKE_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.APPLICATION_ID]: ['string', 'required'],
  [EApplicantOfferPGKeys.STATUS]: ['string', 'required'],
  [EApplicantOfferPGKeys.EMAIL]: ['string', 'required'],
  [EApplicantOfferPGKeys.KNOWN_BY]: ['string', 'required'],
  [EApplicantOfferPGKeys.GIVEN_NAME]: ['string'],
  [EApplicantOfferPGKeys.FAMILY_NAME]: ['string'],
  [EApplicantOfferPGKeys.FEE_STATUS]: ['string'],
  [EApplicantOfferPGKeys.NATIONALITY]: ['string'],
  [EApplicantOfferPGKeys.POSTCODE]: ['string'],
  [EApplicantOfferPGKeys.CITY]: ['string'],
  [EApplicantOfferPGKeys.BIRTH_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.APPLICATION_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.RESIDENCE_COUNTRY]: ['string'],
  [EApplicantOfferPGKeys.COURSE_ID]: ['string'],
  [EApplicantOfferPGKeys.COURSE_NAME]: ['string'],
  [EApplicantOfferPGKeys.MODE]: ['string'],
  [EApplicantOfferPGKeys.ENTRY_POINT]: ['string'],
  [EApplicantOfferPGKeys.APPLIED_DATE]: ['string'],
  [EApplicantOfferPGKeys.ISSUE_DATE]: ['string'],
  [EApplicantOfferPGKeys.CONDITION_TYPE]: ['string'],
  [EApplicantOfferPGKeys.APPLICANT_STATUS]: ['jsonb'],
  [EApplicantOfferPGKeys.PENDING_CHANGES]: ['boolean'],
  [EApplicantOfferPGKeys.REQUIREMENTS_IN_ASSESSMENT]: ['integer'],
  [EApplicantOfferPGKeys.REQUIREMENTS_WITH_CONDITION]: ['integer'],
  [EApplicantOfferPGKeys.APPLICANT_LAST_UPDATED_AT]: ['string'],
  [EApplicantOfferPGKeys.STAGE_CHANGE_DATE]: ['string'],
  [EApplicantOfferPGKeys.NEW_DOCUMENTS_COUNT]: ['integer'],
  [EApplicantOfferPGKeys.AGENT_ID]: ['string'],
  [EApplicantOfferPGKeys.AGENT_NAME]: ['string'],
  [EApplicantOfferPGKeys.TAGS]: ['jsonb'],
  [EApplicantOfferPGKeys.STUDENT_NUMBER]: ['string'],
  [EApplicantOfferPGKeys.AGENT_ACCESS_STATUS]: ['string'],
  [EApplicantOfferPGKeys.PARTNERS]: ['string[]'],
  [EApplicantOfferPGKeys.STATUS_REASON]: ['string'],
  [EApplicantOfferPGKeys.HIGHEST_QUALIFICATION]: ['string'],
  [EApplicantOfferPGKeys.SCHOLARSHIPS_APPLIED]: ['float'],
  [EApplicantOfferPGKeys.POTENTIAL_DUPLICATE]: ['boolean'],
  [EApplicantOfferPGKeys.PHONE_NO]: ['string'],
  [EApplicantOfferPGKeys.DOB]: ['string'],
  [EApplicantOfferPGKeys.STUDY_LEVEL]: ['string'],
};

/**
 * This object maps the relationship between backend filter keys as part of the interface IFilterKeys
 * and the PG record rows.  Not all filters are applicable to the PG record due to the summarised information available
 * on postgres.
 */
export const ApplicantOfferPGFilterMap: { [filterKey: string]: string } = Object.freeze({
  [ApiFilterKeys.NATIONALITY]: EApplicantOfferPGKeys.NATIONALITY,
  [ApiFilterKeys.BIRTH_COUNTRY]: EApplicantOfferPGKeys.BIRTH_COUNTRY,
  [ApiFilterKeys.APPLICATION_COUNTRY]: EApplicantOfferPGKeys.APPLICATION_COUNTRY,
  [ApiFilterKeys.COUNTRY]: EApplicantOfferPGKeys.RESIDENCE_COUNTRY,
  [ApiFilterKeys.FEE_STATUS]: EApplicantOfferPGKeys.FEE_STATUS,
  [ApiFilterKeys.OFFER_STATUS]: EApplicantOfferPGKeys.STATUS,
  [ApiFilterKeys.COURSE]: EApplicantOfferPGKeys.COURSE_NAME,
  [ApiFilterKeys.TAG]: EApplicantOfferPGKeys.TAGS,
});

export enum EApplicationPGKeys {
  COURSE_ID = 'course_id',
  COURSE_NAME = 'course_name',
  KNOWN_BY = 'known_by',
  GIVEN_NAME = 'given_name',
  FAMILY_NAME = 'family_name',
  EMAIL = 'email',
  NATIONALITY = 'nationality',
  BIRTH_COUNTRY = 'birth_country',
  RESIDENCE_COUNTRY = 'residence_country',
  APPLICATION_COUNTRY = 'application_country',
  POSTCODE = 'postcode',
  CITY = 'city',
  APPLICATION_ID = 'application_id',
  FEE_STATUS = 'fee_status',
  STATUS = 'application_status',
  INTAKE_ID = 'intake_id',
  UNI = 'uni',
  USER_ID = 'user_id',
  DOB = 'dob',
  PHONE_NO = 'phone_number',
  UCAS = 'ucas_id',
  GENDER = 'gender',
  STUDY_LEVEL = 'intended_level_of_study',
  AGENT_ID = 'agent_id',
  AGENT_NAME = 'agent_name',
  AGENT_ACCESS_STATUS = 'agent_access_status',
  PARTNERS = 'partners',
  HIGHEST_QUALIFICATION = 'highest_qualification',
  POTENTIAL_DUPLICATE = 'potential_duplicate',
  APPLICANT_LAST_UPDATED_AT = 'applicant_last_updated_at',
  STAGE_CHANGE_DATE = 'stage_change_date',
  // PROGRAMME_CHOICE_PRIORITY = 'programme_choice_priority'
}

// PG Record of "Application" draws information from ApplicantDetails and Application
export type IApplicationPGRecord = {
  p_key: string;
  agent_access_status: AgentApplicantAuthorisationStatus | null;
  partners: string[] | null;
  highest_qualification: string | null;
  // Course-specific fields (flattened from programmeChoices)
  [EApplicationPGKeys.INTAKE_ID]: string;
  [EApplicationPGKeys.STUDY_LEVEL]: string;
  [EApplicationPGKeys.COURSE_ID]: string;
  [EApplicationPGKeys.COURSE_NAME]: string;
  [EApplicationPGKeys.POTENTIAL_DUPLICATE]: boolean;
  [EApplicationPGKeys.STAGE_CHANGE_DATE]?: string | null;
  [EApplicationPGKeys.APPLICANT_LAST_UPDATED_AT]?: string | null;
} & RenameKey<
  Pick<
    CamelToSnake<Application> &
      CamelToSnake<ApplicantDetails['residentialAddress']> &
      CamelToSnake<ApplicantDetails['primaryResidence']>,
    | 'university_name' // universityName
    | 'country_of_birth' // countryOfBirth
    | 'applying_from' // applyingFrom
    | 'country_of_residence' // primaryResidence -> countryOfResidence
    | EApplicationPGKeys.USER_ID
    | EApplicationPGKeys.APPLICATION_ID
    | EApplicationPGKeys.EMAIL
    | EApplicationPGKeys.KNOWN_BY
    | EApplicationPGKeys.GIVEN_NAME
    | EApplicationPGKeys.FAMILY_NAME
    | EApplicationPGKeys.FEE_STATUS
    | EApplicationPGKeys.NATIONALITY
    | EApplicationPGKeys.DOB
    | EApplicationPGKeys.PHONE_NO
    | EApplicationPGKeys.UCAS
    | EApplicationPGKeys.GENDER
    | EApplicationPGKeys.STATUS
    | EApplicationPGKeys.POSTCODE
    | EApplicationPGKeys.CITY
    | EApplicationPGKeys.AGENT_ID
    | EApplicationPGKeys.AGENT_NAME
  >,
  {
    university_name: EApplicationPGKeys.UNI;
    applying_from: EApplicationPGKeys.APPLICATION_COUNTRY;
    country_of_birth: EApplicationPGKeys.BIRTH_COUNTRY;
    country_of_residence: EApplicationPGKeys.RESIDENCE_COUNTRY;
    agent_access_status: EApplicationPGKeys.AGENT_ACCESS_STATUS;
    highest_qualification: EApplicationPGKeys.HIGHEST_QUALIFICATION;
    applicant_last_updated_at: EApplicationPGKeys.APPLICANT_LAST_UPDATED_AT;
    stage_change_date: EApplicationPGKeys.STAGE_CHANGE_DATE;
  }
>;

export const IApplicationPGTypeMap: Record<
  keyof IApplicationPGRecord,
  Array<'string' | 'string[]' | 'integer' | 'float' | 'boolean' | 'jsonb' | 'date' | 'required'>
> = {
  p_key: ['string', 'required'],
  [EApplicationPGKeys.UNI]: ['string', 'required'],
  [EApplicationPGKeys.USER_ID]: ['string', 'required'],
  [EApplicationPGKeys.APPLICATION_ID]: ['string', 'required'],
  [EApplicationPGKeys.EMAIL]: ['string', 'required'],
  [EApplicationPGKeys.KNOWN_BY]: ['string', 'required'],
  [EApplicationPGKeys.GIVEN_NAME]: ['string'],
  [EApplicationPGKeys.FAMILY_NAME]: ['string'],
  [EApplicationPGKeys.FEE_STATUS]: ['string'],
  [EApplicationPGKeys.NATIONALITY]: ['string'],
  [EApplicationPGKeys.POSTCODE]: ['string'],
  [EApplicationPGKeys.CITY]: ['string'],
  [EApplicationPGKeys.BIRTH_COUNTRY]: ['string'],
  [EApplicationPGKeys.APPLICATION_COUNTRY]: ['string'],
  [EApplicationPGKeys.RESIDENCE_COUNTRY]: ['string'],
  [EApplicationPGKeys.DOB]: ['string'],
  [EApplicationPGKeys.PHONE_NO]: ['string'],
  [EApplicationPGKeys.UCAS]: ['string'],
  [EApplicationPGKeys.GENDER]: ['string'],
  [EApplicationPGKeys.STATUS]: ['string'],
  [EApplicationPGKeys.INTAKE_ID]: ['string'],
  [EApplicationPGKeys.STUDY_LEVEL]: ['string'],
  [EApplicationPGKeys.COURSE_ID]: ['string'],
  [EApplicationPGKeys.COURSE_NAME]: ['string'],
  [EApplicationPGKeys.AGENT_ID]: ['string'],
  [EApplicationPGKeys.AGENT_NAME]: ['string'],
  [EApplicationPGKeys.AGENT_ACCESS_STATUS]: ['string'],
  [EApplicationPGKeys.PARTNERS]: ['string[]'],
  [EApplicationPGKeys.HIGHEST_QUALIFICATION]: ['string'],
  [EApplicationPGKeys.POTENTIAL_DUPLICATE]: ['boolean'],
  [EApplicationPGKeys.STAGE_CHANGE_DATE]: ['string'],
  [EApplicationPGKeys.APPLICANT_LAST_UPDATED_AT]: ['string'],
};

export const applicationPGKeys = Object.keys(IApplicationPGTypeMap) as Array<keyof IApplicationPGRecord>;

/**
 * This object maps the relationship between backend filter keys as part of the interface IFilterKeys
 * and the PG record rows.  Not all filters are applicable to the PG record due to the summarised information available
 * on postgres.
 */
export const ApplicationPGFilterMap: { [filterKey: string]: string } = Object.freeze({
  [ApiFilterKeys.NATIONALITY]: EApplicationPGKeys.NATIONALITY,
  [ApiFilterKeys.BIRTH_COUNTRY]: EApplicationPGKeys.BIRTH_COUNTRY,
  [ApiFilterKeys.APPLICATION_COUNTRY]: EApplicationPGKeys.APPLICATION_COUNTRY,
  [ApiFilterKeys.COUNTRY]: EApplicationPGKeys.RESIDENCE_COUNTRY,
  [ApiFilterKeys.COURSE]: EApplicationPGKeys.COURSE_NAME,
  [ApiFilterKeys.FEE_STATUS]: EApplicationPGKeys.FEE_STATUS,
  [ApiFilterKeys.OFFER_STATUS]: EApplicationPGKeys.STATUS,
});

export type SharedPGKeys = EApplicantOfferPGKeys | EApplicationPGKeys;

export type IGradingSystemPGRecord = {
  id: number;
  label: string;
};
export type IQualificationTypesPGRecord = {
  id: number;
  label: string;
  awarding_organisations_visible: boolean;
  modules_visible: boolean;
  subjects_visible: boolean;
  grading_systems: IGradingSystemPGRecord[];
};

export type IAwardingOrganisationPGRecord = {
  id: number;
  label: string;
  acronym: string;
};

// Return types from postgres queries

export type IApplicationSummaryTableRow = {
  intakeId: string;
  applicationId: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  feeStatus: FeeStatus | null;
  nationality: string | null;
  dob: string | null;
  phoneNumber: string | null;
  ucasId: string | null;
  gender: string | null;
  applicationStatus: ApplicationStatus | null;
  studyLevel: StudyLevel | null;
  courseId: string | null;
  courseName: string | null;
  postcode: string | null;
  uni: string | null;
  userId: string | null;
  agentId: string | null;
  agentName: string | null;
  knownBy: string | null;
  agentAccessStatus: AgentApplicantAuthorisationStatus | null;
  partners: string[] | null;
  highestQualification: string | null;
  potentialDuplicate: boolean | null;
  applicantLastUpdatedAt: string | null;
  stageChangeDate: string | null;
};

export type IApplicantOfferSummaryTableRow = {
  uni: string;
  userId: string;
  offerId: string | null;
  givenName: string | null;
  familyName: string | null;
  email: string | null;
  phoneNumber: string | null;
  dob: string | null;
  nationality: string | null;
  birthCountry: string | null;
  applicationCountry: string | null;
  countryOfResidence: string | null;
  intakeId: string | null;
  applicationId: string | null;
  status: ApplicantOfferStatus | null;
  feeStatus: FeeStatus | null;
  appliedDate: string | null;
  issueDate: string | null;
  conditionType: ApplicantOfferConditionType | null;
  applicantStatus: ApplicantResponseStatus | null;
  pendingChanges: boolean | null;
  requirementsInAssessment: number | null;
  requirementsWithCondition: number | null;
  applicantLastUpdatedAt: string | null;
  stageChangeDate: string | null;
  newDocumentsCount: number | null;
  agentId: string | null;
  agentName: string | null;
  tags: ITag[] | null;
  programme: {
    courseId: string | null;
    courseName: string | null;
    mode: StudyMode | null;
    entryPoint: EntryPoint | null;
  };
  agentAccessStatus: AgentApplicantAuthorisationStatus | null;
  studentNo: string | null;
  statusReason: string | null;
  highestQualification: string | null;
  scholarshipsApplied: number | null;
  potentialDuplicate: boolean | null;
  studyLevel: StudyLevel | null;
};
